<script setup>
import { computed, inject } from 'vue';
import { pageHeaderHasContent, pageHeaderTitle, starterTitleStyle } from '../blockContent.js';
import { accentParts, hasLink, isExternal, styleVars } from './shared.js';
import { trailTotalsLine } from '../trailsPage.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

const TONES = ['tint', 'dark', 'plain'];
const tone = computed(() => (TONES.includes(props.config.tone) ? props.config.tone : 'tint'));
const titleParts = computed(() => accentParts(pageHeaderTitle(props.config, editing)));
const cta = computed(() => props.config.cta || {});
// 'trail_totals' writes "4 networks · 8 trails · 23.9 miles" from the
// site's own trails, so the line cannot go stale.
const eyebrow = computed(() => (props.config.eyebrow_source === 'trail_totals'
    ? trailTotalsLine(props.site_data)
    : props.config.eyebrow));
// A header with nothing but the starter "Page title" draws nothing for
// a visitor; the editor keeps it (Mus, 2026-10-11).
const editing = inject('tiqEditorCanvas', false);
const hasContent = computed(() => editing || pageHeaderHasContent(props.config, eyebrow.value));
</script>

<template>
    <section v-if="hasContent" :id="config.anchor_id || null"
             class="th-block th-pagehead"
             :class="{ 'th-tint': tone === 'tint', 'th-dark': tone === 'dark' }"
             :style="styleVars(config)">
        <div class="th-wrap th-pagehead__inner">
            <div class="th-pagehead__text">
                <p v-if="eyebrow" class="th-eyebrow th-pagehead__eyebrow">{{ eyebrow }}</p>
                <h1 v-if="titleParts.length || editing" class="th-h1 th-h1--page" :style="starterTitleStyle(config, editing)">
                    <template v-for="(part, i) in titleParts" :key="i">
                        <span v-if="part.accent" class="th-accent-word">{{ part.text }}</span>
                        <template v-else>{{ part.text }}</template>
                    </template>
                </h1>
                <p v-if="config.subtitle" class="th-lede th-pagehead__sub">{{ config.subtitle }}</p>
            </div>
            <a v-if="hasLink(cta)"
               class="th-btn th-pagehead__cta"
               :class="tone === 'dark' ? 'th-btn--light' : 'th-btn--primary'"
               :href="cta.href || '#'"
               :target="isExternal(cta.href) ? '_blank' : null"
               :rel="isExternal(cta.href) ? 'noopener noreferrer' : null">{{ cta.label }}</a>
        </div>
    </section>
</template>

<style scoped>
.th-pagehead__inner {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    gap: 24px;
    padding-top: clamp(40px, 6vw, 80px);
    padding-bottom: clamp(28px, 4vw, 48px);
}
.th-pagehead__text { min-width: 0; }
.th-pagehead__sub { max-width: 56ch; }
.th-pagehead__cta { flex: none; }
/* The dark tone's eyebrow is the paper grey of the mockup rather than
   the accent, which does not carry on near-black. */
.th-dark.th-pagehead .th-pagehead__eyebrow { color: var(--text-eyebrow, #c8ccc5); }
</style>
